import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import koCommon from './locales/ko/common.json'
import enCommon from './locales/en/common.json'
import { languageCookieDomain } from './cookieDomain'

/**
 * 다국어 설정.
 *
 * 지원 언어를 늘릴 때는 locales/<code>/common.json 을 추가하고
 * SUPPORTED_LANGUAGES 에 등록하면 된다. 그 외 코드 변경은 필요 없다.
 */

export const SUPPORTED_LANGUAGES = [
  { code: 'ko', label: 'Ko' },
  { code: 'en', label: 'En' },
] as const

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]['code']

export const DEFAULT_LANGUAGE: SupportedLanguage = 'ko'
export const LANGUAGE_STORAGE_KEY = 'shorty-language'

const resources = {
  ko: { common: koCommon },
  en: { common: enCommon },
}

/** 지원 목록에 있는 언어인지 확인 (외부 입력 정규화용) */
export function isSupportedLanguage(
  value: unknown
): value is SupportedLanguage {
  return SUPPORTED_LANGUAGES.some(language => language.code === value)
}

/** 'ko-KR' 처럼 지역 코드가 붙어 있어도 지원 언어로 좁힌다 */
export function resolveLanguage(value?: string | null): SupportedLanguage {
  if (!value) {
    return DEFAULT_LANGUAGE
  }
  const base = value.split('-')[0].toLowerCase()
  return isSupportedLanguage(base) ? base : DEFAULT_LANGUAGE
}

if (!i18n.isInitialized) {
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng: DEFAULT_LANGUAGE,
      supportedLngs: SUPPORTED_LANGUAGES.map(language => language.code),
      // 'ko-KR' 같은 값을 'ko' 로 접어 리소스를 찾게 한다
      load: 'languageOnly',
      defaultNS: 'common',
      ns: ['common'],
      detection: {
        // 쿠키를 먼저 본다 — shorty.kr 과 app.shorty.kr 이 설정을 공유한다.
        // localStorage 는 origin 단위라 도메인을 넘지 못한다.
        order: ['cookie', 'localStorage', 'navigator', 'htmlTag'],
        lookupCookie: LANGUAGE_STORAGE_KEY,
        lookupLocalStorage: LANGUAGE_STORAGE_KEY,
        caches: ['cookie', 'localStorage'],
        cookieDomain: languageCookieDomain(
          typeof window !== 'undefined' ? window.location.hostname : undefined
        ),
        cookieOptions: {
          path: '/',
          sameSite: 'lax',
          // 1년
          maxAge: 365 * 24 * 60 * 60,
        },
      },
      interpolation: {
        // React 가 이미 이스케이프하므로 이중 처리하지 않는다
        escapeValue: false,
      },
    })
}

export default i18n
